/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the "Elastic License
 * 2.0", the "GNU Affero General Public License v3.0 only", and the "Server Side
 * Public License v 1"; you may not use this file except in compliance with, at
 * your election, the "Elastic License 2.0", the "GNU Affero General Public
 * License v3.0 only", or the "Server Side Public License, v 1".
 */

import { CharStreams } from 'antlr4';
import { CommonTokenStream } from 'antlr4';
import { ESQLErrorListener } from './esql_error_listener';
import { ESQLAstBuilderListener } from './esql_ast_builder_listener';
import { GRAMMAR_ROOT_RULE } from './constants';
import { attachDecorations, collectDecorations } from './formatting';
import { Builder } from '../builder';
import { default as ESQLLexer } from '../antlr/esql_lexer';
import { default as ESQLParser } from '../antlr/esql_parser';
/**
 * Some changes to the grammar deleted the literal names for some tokens.
 * This is a workaround to restore the literals that were lost.
 *
 * See https://github.com/elastic/elasticsearch/pull/124177 for context.
 */
const replaceSymbolsWithLiterals = (symbolicNames, literalNames) => {
  const symbolReplacements = new Map([['LP', '('], ['OPENING_BRACKET', '[']]);
  for (let i = 0; i < symbolicNames.length; i++) {
    const name = symbolicNames[i];
    if (name && symbolReplacements.has(name)) {
      literalNames[i] = `'${symbolReplacements.get(name)}'`;
    }
  }
};
export const getLexer = (inputStream, errorListener) => {
  const lexer = new ESQLLexer(inputStream);
  replaceSymbolsWithLiterals(lexer.symbolicNames, lexer.literalNames);
  lexer.removeErrorListeners();
  lexer.addErrorListener(errorListener);
  return lexer;
};
export const getParser = (inputStream, errorListener, parseListener) => {
  const lexer = getLexer(inputStream, errorListener);
  const tokens = new CommonTokenStream(lexer);
  const parser = new ESQLParser(tokens);
  replaceSymbolsWithLiterals(parser.symbolicNames, parser.literalNames);
  parser.removeErrorListeners();
  parser.addErrorListener(errorListener);
  if (parseListener) {
    // @ts-expect-error the addParseListener API does exist and is documented here
    // https://github.com/antlr/antlr4/blob/dev/doc/listeners.md
    parser.addParseListener(parseListener);
  }
  return {
    lexer,
    tokens,
    parser
  };
};
export const createParser = src => {
  const errorListener = new ESQLErrorListener();
  const parseListener = new ESQLAstBuilderListener(src);
  return getParser(CharStreams.fromString(src), errorListener, parseListener);
};
export const parse = (text, options = {}) => {
  try {
    if (text == null) {
      const commands = [];
      return {
        ast: commands,
        root: Builder.expression.query(commands),
        errors: [],
        tokens: []
      };
    }
    const errorListener = new ESQLErrorListener();
    const parseListener = new ESQLAstBuilderListener(text);
    const {
      tokens,
      parser
    } = getParser(CharStreams.fromString(text), errorListener, parseListener);
    parser[GRAMMAR_ROOT_RULE]();
    const errors = errorListener.getErrors();
    const {
      ast: commands
    } = parseListener.getAst();
    const root = Builder.expression.query(commands, {
      location: {
        min: 0,
        max: text.length - 1
      }
    });
    if (options.withFormatting) {
      const decorations = collectDecorations(tokens);
      attachDecorations(root, tokens.tokens, decorations.lines);
    }
    return {
      root,
      ast: commands,
      errors,
      tokens: tokens.tokens
    };
  } catch (error) {
    if (error !== 'Empty Stack')
      // eslint-disable-next-line no-console
      console.error(error);
    const root = Builder.expression.query();
    return {
      root,
      ast: root.commands,
      errors: [{
        startLineNumber: 0,
        endLineNumber: 0,
        startColumn: 0,
        endColumn: 0,
        message: `Invalid query [${text}]`,
        severity: 'error'
      }],
      tokens: []
    };
  }
};
export const parseErrors = text => {
  const errorListener = new ESQLErrorListener();
  const {
    parser
  } = getParser(CharStreams.fromString(text), errorListener);
  parser[GRAMMAR_ROOT_RULE]();
  return errorListener.getErrors();
};